'use client';

import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList
} from 'recharts';
import { ChefHat, Package } from 'lucide-react';

const productionData = [
  { id: 'prod-small', size: 'Small (250g)', count: 22, color: 'var(--primary)', ingredient: '5.5 kg chickpeas' },
  { id: 'prod-medium', size: 'Medium (500g)', count: 16, color: 'var(--accent)', ingredient: '8.0 kg chickpeas' },
  { id: 'prod-large', size: 'Large (1kg)', count: 11, color: 'var(--positive)', ingredient: '11.0 kg chickpeas' },
  { id: 'prod-family', size: 'Family (2kg)', count: 7, color: 'var(--warning)', ingredient: '14.0 kg chickpeas' },
];

const ingredientForecast = [
  { id: 'ing-chickpeas', name: 'Chickpeas', needed: '38.5 kg', unit: 'kg', status: 'ok' },
  { id: 'ing-tahini', name: 'Tahini', needed: '7.2 L', unit: 'L', status: 'low' },
  { id: 'ing-olive-oil', name: 'Olive Oil', needed: '2.8 L', unit: 'L', status: 'ok' },
  { id: 'ing-garlic', name: 'Garlic', needed: '560 g', unit: 'g', status: 'ok' },
  { id: 'ing-lemon', name: 'Lemon Juice', needed: '1.4 L', unit: 'L', status: 'ok' },
  { id: 'ing-containers', name: 'Containers (all sizes)', needed: '56 pcs', unit: 'pcs', status: 'critical' },
  { id: 'ing-labels', name: 'Labels', needed: '56 pcs', unit: 'pcs', status: 'low' },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; payload: { size: string; ingredient: string } }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="card-elevated p-3 min-w-[160px]">
      <p className="text-sm font-600 text-foreground">{d.payload.size}</p>
      <p className="text-2xl font-700 font-tabular text-primary">{d.value}</p>
      <p className="text-xs text-muted-foreground mt-1">{d.payload.ingredient}</p>
    </div>
  );
}

export default function ProductionForecast() {
  const total = productionData.reduce((s, d) => s + d.count, 0);

  return (
    <div className="card-elevated p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-primary/10 text-primary">
            <ChefHat size={18} />
          </div>
          <div>
            <h2 className="text-base font-700 text-foreground">Tomorrow&apos;s Production</h2>
            <p className="text-xs text-muted-foreground">Thursday, 31 July 2026</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-700 font-tabular text-primary">{total}</p>
          <p className="text-xs text-muted-foreground">Total containers</p>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="h-44 mb-5">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={productionData} barSize={36} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="size"
              tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-sans)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: string) => v.split(' ')[0]}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-sans)' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.4 }} />
            <Bar dataKey="count" radius={[6, 6, 0, 0]}>
              {productionData.map((entry) => (
                <Cell key={entry.id} fill={entry.color} />
              ))}
              <LabelList dataKey="count" position="top" style={{ fontSize: 12, fontWeight: 700, fill: 'var(--foreground)', fontFamily: 'var(--font-sans)' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Ingredient Forecast */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Package size={14} className="text-muted-foreground" />
          <p className="text-xs font-600 uppercase tracking-wider text-muted-foreground">Ingredient Forecast</p>
        </div>
        <div className="space-y-2">
          {ingredientForecast.map(ing => (
            <div key={ing.id} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-muted/50">
              <span className="text-sm text-foreground">{ing.name}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-600 font-tabular text-foreground">{ing.needed}</span>
                {ing.status === 'critical' && (
                  <span className="status-badge bg-negative-bg text-negative text-[10px]">OUT OF STOCK</span>
                )}
                {ing.status === 'low' && (
                  <span className="status-badge bg-warning-bg text-warning text-[10px]">LOW</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}